import React, { useEffect, useState } from 'react'
import './App.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import {
	TextField,
	Button,
	Checkbox,
	FormControlLabel,
	CircularProgress,
	MenuItem,
	Box,
	CardContent,
	Card,
	Typography,
	Stack,
	Chip,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'

interface TelegramUser {
	id: number
	first_name: string
	last_name?: string
	username?: string
	photo_url?: string
	language_code?: string
	allows_write_to_pm?: boolean
}

interface TelegramWebApp {
	initData: string
	initDataUnsafe: {
		user?: TelegramUser
		auth_date?: number
		hash?: string
	}
	close(): void
	expand(): void
	sendData(data: string): void
}

declare global {
	interface Window {
		Telegram: {
			WebApp: TelegramWebApp
		}
	}
}

const validationSchema = Yup.object({
	first_name: Yup.string().required('Обов\'язкове поле'),
	last_name: Yup.string().required('Обов\'язкове поле'),
	username: Yup.string().required('Обов\'язкове поле'),
	experience_text: Yup.string().required('Обов\'язкове поле'),
	age: Yup.number().nullable().min(1, 'Вік має бути більше 0'),
	desired_position: Yup.string().required('Обов\'язкове поле'),
	skills: Yup.array().min(1, 'Має бути хоча б один навик'),
})

function App() {
	const [loading, setLoading] = useState(false)
	const tg: TelegramWebApp = window.Telegram.WebApp
	const [error, setError] = useState('')
	const [skill, setSkill] = useState('')
	const [lang, setLang] = useState('')
	const [token, setToken] = useState('')
	const formik = useFormik({
		initialValues: {
			first_name: '',
			last_name: '',
			username: '',
			age: null,
			experience_text: '',
			desired_position: '',
			desired_salary: '',
			location: '',
			skills: [],
			languages: [],
			education_level: '',
			employment_type: '',
			schedule: '',
			is_remote: false,
		},
		validationSchema,
		onSubmit: async (values) => {
			try {
				const response = await axios.put(
					`${process.env.REACT_APP_API_URL}/api/profile/${tg.initDataUnsafe.user.id}/`,
					values,
					{
						headers: { Authorization: `Bearer ${token}` },
					},
				)
				if (response.status === 200) {
					tg.close()
				}
			} catch (err: any) {
				if (err.response?.data) {
					const serverErrors = err.response.data
					Object.keys(serverErrors).forEach((key) => {
						formik.setFieldError(key, serverErrors[key][0])
					})
				}
			}
		},
	})

	const addSkill = (type: 'skills' | 'languages', value: string) => {
		const trimmed = value.trim()
		if (trimmed !== '' && !formik.values[type].includes(trimmed)) {
			formik.setFieldValue(type, [...formik.values[type], trimmed])
			if (type === 'skills') setSkill('')
			else setLang('')
		}
	}

	const handleKeyPress = (
		e: React.KeyboardEvent,
		type: 'skills' | 'languages',
	) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (type === 'skills') addSkill(type, skill)
			if (type === 'languages') addSkill(type, lang)
		}
	}

	const removeSkill = (type: 'skills' | 'languages', skill: string) => {
		formik.setFieldValue(
			type,
			formik.values[type].filter((s) => s !== skill),
		)
	}

	useEffect(() => {
		tg.expand()
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/telegram/auth/`,
				{
					initData: tg.initData,
				},
				{
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
				},
			)
			.then((response) => {
				setToken(response.data.token)
				axios
					.get(
						`${process.env.REACT_APP_API_URL}/api/profile/${tg.initDataUnsafe.user.id}/`,
						{ headers: { Authorization: `Bearer ${response.data.token}` } },
					)
					.then((res) => {
						formik.setValues(res.data)
					})
					.catch(() => setError('Не вдалося завантажити дані'))
					.finally(() => setLoading(false))
			})
			.catch((error) => {
				setError('Не вдалося авторизуватися')
				setLoading(false)
			})
	}, [])

	if (!tg.initData) {
		return <> </>
	}

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			{loading ? (
				<CircularProgress />
			) : error ? (
				<p>{error}</p>
			) : (
				<Card sx={{ maxWidth: '700px', m: 2 }}>
					<CardContent>
						<Typography textAlign="center" variant="h5" mr={4}>
							Данні профілю
						</Typography>
						<form onSubmit={formik.handleSubmit}>
							<TextField
								margin="normal"
								fullWidth
								label="Ім'я"
								{...formik.getFieldProps('first_name')}
								error={
									formik.touched.first_name && Boolean(formik.errors.first_name)
								}
								helperText={
									formik.touched.first_name && formik.errors.first_name
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Прізвище"
								{...formik.getFieldProps('last_name')}
								error={
									formik.touched.last_name && Boolean(formik.errors.last_name)
								}
								helperText={formik.touched.last_name && formik.errors.last_name}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Username"
								{...formik.getFieldProps('username')}
								error={
									formik.touched.username && Boolean(formik.errors.username)
								}
								helperText={formik.touched.username && formik.errors.username}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Вік"
								{...formik.getFieldProps('age')}
								value={formik.values.age ? formik.values.age.toString() : ''}
								error={formik.touched.age && Boolean(formik.errors.age)}
								helperText={
									formik.touched.age && Array.isArray(formik.errors.age)
										? formik.errors.age.join(', ')
										: typeof formik.errors.age === 'string'
											? formik.errors.age
											: ''
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Досвід"
								{...formik.getFieldProps('experience_text')}
								error={
									formik.touched.experience_text &&
									Boolean(formik.errors.experience_text)
								}
								helperText={
									formik.touched.experience_text &&
									formik.errors.experience_text
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Бажана позиція"
								{...formik.getFieldProps('desired_position')}
								error={
									formik.touched.desired_position &&
									Boolean(formik.errors.desired_position)
								}
								helperText={
									formik.touched.desired_position &&
									formik.errors.desired_position
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Бажана зарплата"
								{...formik.getFieldProps('desired_salary')}
								error={
									formik.touched.desired_salary &&
									Boolean(formik.errors.desired_salary)
								}
								helperText={
									formik.touched.desired_salary && formik.errors.desired_salary
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Локація"
								{...formik.getFieldProps('location')}
								error={
									formik.touched.location && Boolean(formik.errors.location)
								}
								helperText={formik.touched.location && formik.errors.location}
							/>
							<Box display="flex" gap={1} mt={1}>
								<TextField
									fullWidth
									label="Додати навик"
									value={skill}
									onChange={(e) => setSkill(e.target.value)}
									onKeyDown={(e) => handleKeyPress(e, 'skills')}
									onBlur={() => formik.handleBlur('skills')}
									error={formik.touched.skills && Boolean(formik.errors.skills)}
									helperText={
										formik.touched.skills && Array.isArray(formik.errors.skills)
											? formik.errors.skills.join(', ')
											: typeof formik.errors.skills === 'string'
												? formik.errors.skills
												: ''
									}
								/>
								<Button
									variant="contained"
									onClick={() => addSkill('skills', skill)}
								>
									Додати
								</Button>
							</Box>
							<Stack direction="row" gap={1} mt={1} flexWrap="wrap">
								{formik.values.skills.map((skill, idx) => (
									<Chip
										key={idx}
										label={skill}
										onDelete={() => removeSkill('skills', skill)}
									/>
								))}
							</Stack>
							<Box display="flex" gap={1} mt={1}>
								<TextField
									fullWidth
									label="Додати мову"
									value={lang}
									onChange={(e) => setLang(e.target.value)}
									onKeyDown={(e) => handleKeyPress(e, 'languages')}
									onBlur={() => formik.handleBlur('languages')}
									error={
										formik.touched.languages && Boolean(formik.errors.languages)
									}
									helperText={
										formik.touched.languages &&
										Array.isArray(formik.errors.languages)
											? formik.errors.languages.join(', ')
											: typeof formik.errors.languages === 'string'
												? formik.errors.languages
												: ''
									}
								/>
								<Button
									variant="contained"
									onClick={() => addSkill('languages', lang)}
								>
									Додати
								</Button>
							</Box>
							<Stack direction="row" gap={1} mt={1} flexWrap="wrap">
								{formik.values.languages.map((lang, idx) => (
									<Chip
										key={idx}
										label={lang}
										variant="outlined"
										onDelete={() => removeSkill('languages', lang)}
									/>
								))}
							</Stack>
							<TextField
								margin="normal"
								fullWidth
								label="Рівень освіти"
								{...formik.getFieldProps('education_level')}
								error={
									formik.touched.education_level &&
									Boolean(formik.errors.education_level)
								}
								helperText={
									formik.touched.education_level &&
									formik.errors.education_level
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Тип зайнятості"
								{...formik.getFieldProps('employment_type')}
								error={
									formik.touched.employment_type &&
									Boolean(formik.errors.employment_type)
								}
								helperText={
									formik.touched.employment_type &&
									formik.errors.employment_type
								}
							/>
							<TextField
								margin="normal"
								fullWidth
								label="Графік"
								{...formik.getFieldProps('schedule')}
								error={
									formik.touched.schedule && Boolean(formik.errors.schedule)
								}
								helperText={formik.touched.schedule && formik.errors.schedule}
							/>
							<FormControlLabel
								sx={{ width: '100%', mb: 2 }}
								control={
									<Checkbox
										{...formik.getFieldProps('is_remote')}
										checked={formik.values.is_remote}
									/>
								}
								label="Віддалено"
							/>
							<Button fullWidth type="submit" variant="contained">
								Зберегти
							</Button>
						</form>
					</CardContent>
				</Card>
			)}
		</Box>
	)
}

export default App
